import { lazy, Suspense, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";
import { useCredits } from "@/hooks/useCredits";
import { Coins } from "lucide-react";

const NavbarAuthSection = lazy(() => import("./NavbarAuthSection"));

const AuthFallback = () => (
  <div className="hidden min-[376px]:flex items-center gap-3">
    <Skeleton className="h-8 w-24 rounded-full" />
  </div>
);

interface NavbarProps {
  onUpgradeClick?: () => void;
  creditsUpdating?: boolean;
}

const Navbar = ({ creditsUpdating = false }: NavbarProps) => {
  const { credits, loading: creditsLoading } = useCredits();
  const [elevated, setElevated] = useState(false);

  useEffect(() => {
    const getRoots = () => Array.from(document.querySelectorAll<HTMLElement>("[data-vml-scroll-root]"));

    const computeElevated = () => {
      const maxScroll = getRoots().reduce((m, el) => Math.max(m, el.scrollTop), 0);
      setElevated(maxScroll > 8);
    };

    let attached: HTMLElement[] = [];
    let moRaf = 0;

    /** Re-query scroll roots and attach listeners (Studio swaps roots by tab without remounting Navbar). */
    const syncScrollRoots = () => {
      const next = getRoots();
      const same =
        next.length === attached.length && next.every((el, i) => el === attached[i]);
      if (same) {
        computeElevated();
        return;
      }
      for (const el of attached) {
        el.removeEventListener("scroll", computeElevated);
      }
      attached = next;
      for (const el of attached) {
        el.addEventListener("scroll", computeElevated, { passive: true });
      }
      computeElevated();
    };

    const scheduleSync = () => {
      cancelAnimationFrame(moRaf);
      moRaf = requestAnimationFrame(() => {
        moRaf = 0;
        syncScrollRoots();
      });
    };

    syncScrollRoots();

    const observeTarget = document.getElementById("root") ?? document.body;
    const mo = new MutationObserver(() => {
      scheduleSync();
    });
    mo.observe(observeTarget, { childList: true, subtree: true });

    window.addEventListener("resize", computeElevated);

    return () => {
      cancelAnimationFrame(moRaf);
      mo.disconnect();
      for (const el of attached) {
        el.removeEventListener("scroll", computeElevated);
      }
      window.removeEventListener("resize", computeElevated);
    };
  }, []);

  return (
    <nav
      className="sticky top-0 z-50 backdrop-blur-[2px] transition-shadow duration-200"
      style={{
        backgroundColor: "rgba(255,255,255,0.96)",
        borderBottom: "1px solid rgba(15,23,42,0.08)",
        boxShadow: elevated ? "0 1px 2px rgba(15,23,42,0.04), 0 8px 24px rgba(15,23,42,0.06)" : "none",
      }}
    >
      <div className="mx-auto flex h-14 max-w-7xl items-center justify-between px-4 sm:px-6">
        <Link
          to="/"
          className="flex min-w-0 items-center gap-2 shrink-0"
          aria-label="Vinci Writer — back to homepage"
        >
          <div
            aria-hidden
            className="flex items-center justify-center shrink-0"
            style={{
              width: 30,
              height: 30,
              borderRadius: 7,
              background: "linear-gradient(135deg, #6366f1 0%, #3b82f6 100%)",
              boxShadow: "0 1px 2px rgba(15,23,42,0.04), 0 8px 24px rgba(99,102,241,0.25)",
            }}
          >
            <span
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontWeight: 900,
                fontSize: 16,
                color: "#fff",
                lineHeight: 1,
              }}
            >
              V
            </span>
          </div>
          <span
            className="max-[375px]:hidden truncate"
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontWeight: 700,
              fontSize: 15,
              color: "#0f172a",
              lineHeight: 1,
              letterSpacing: "-0.01em",
            }}
          >
            Vinci Writer
          </span>
        </Link>

        <div className="flex min-w-0 items-center gap-2">
          {!creditsLoading && credits && typeof credits.balance === "number" && (
            <Link
              to="/settings#billing"
              className="flex md:hidden max-w-[140px] min-[376px]:max-w-none items-center gap-1 rounded-full px-2.5 py-1 min-[376px]:px-3 transition-all duration-150 hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(99,102,241,0.18)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#6366F1] no-underline"
              style={{
                backgroundColor: credits.balance > 0 ? "rgba(99,102,241,0.08)" : "#F1F5F9",
                border: `1px solid ${credits.balance > 0 ? "rgba(99,102,241,0.25)" : "rgba(15,23,42,0.08)"}`,
                boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
                fontSize: 12,
                fontWeight: 600,
                color: credits.balance > 0 ? "#4F46E5" : "#334155",
                minHeight: 30,
                fontVariantNumeric: "tabular-nums",
                textDecoration: "none",
              }}
              aria-label="Credits remaining, tap to manage billing"
            >
              {creditsUpdating ? (
                <span
                  aria-hidden
                  className="inline-block h-2.5 w-2.5 shrink-0 animate-spin rounded-full border-2 border-slate-400 border-t-transparent"
                />
              ) : (
                <>
                  <Coins size={12} aria-hidden className="shrink-0" />
                  <span className="tabular-nums">{credits.balance}</span>
                </>
              )}
            </Link>
          )}

          <Suspense fallback={<AuthFallback />}>
            <NavbarAuthSection />
          </Suspense>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
